import {Injectable, WritableSignal} from "@angular/core";

import {AppService} from "app/app.service";
import * as Models from "app/services/proxy/model/models";
import {FeatureFlagsHelper} from "app/services/third-party/feature-flags/feature-flags-helper.service";

import {Lookup} from "framework/services/utils.service";

import * as LDClient from "launchdarkly-js-client-sdk";

@Injectable()
export class LaunchDarklyFeatureFlagsProvider
{
    // Error messages
    private static readonly ERR_QUEUE_FLAG_FAILED        = (flagName: string) =>
        `[LaunchDarkly] Error queuing feature flag '${flagName}':`;
    private static readonly ERR_QUEUE_FLAGS_FAILED       = "[LaunchDarkly] Error queuing all flags:";
    private static readonly ERR_INVALID_FLAG             = "Invalid Flag!";
    private static readonly ERR_INVALID_FEATURE_CATEGORY = "Invalid feature category!";

    // Log messages
    private static readonly LOG_INITIALIZED  = "[LaunchDarkly] Initialized";
    private static readonly LOG_CLIENT_READY = "[LaunchDarkly] Client is ready";

    // Warning messages
    private static readonly WARN_CLIENT_ERROR = "[LaunchDarkly] Client error:";

    //--// State
    private m_initialized                                                                                   = false;
    private m_initializing                                                                                  = false;
    private m_ldClient: LDClient.LDClient | null                                                            = null;
    private m_currentContext: LDClient.LDContext | null                                                     = null;
    private m_releaseFlagState: WritableSignal<Lookup<Models.ReleaseFlag>>                                  = null;
    private m_featureFlagState: WritableSignal<Lookup<Models.FeatureFlag>>                                  = null;
    private m_pendingSingleRequests: Array<{ request: Models.FeatureFlagRequestBase; resolve: () => void }> = [];
    private m_pendingAllRequests: Array<{ request: Models.FeatureFlagRequestBase; resolve: () => void }>    = [];

    //--// LaunchDarkly event constants
    private static readonly LD_EVENT_INITIALIZED = "initialized";
    private static readonly LD_EVENT_READY       = "ready";
    private static readonly LD_EVENT_CHANGE      = "change";
    private static readonly LD_EVENT_ERROR       = "error";

    //--// Private helpers
    private get isLdEnabled(): boolean
    {
        return !!AppService.config.ldClientId;
    }

    private updateContextIfChanged(context: Models.FeatureFlagContext): void
    {
        if (!context || !this.m_ldClient) return;

        const ldContext = FeatureFlagsHelper.translateToLDContext(context);

        if (FeatureFlagsHelper.hasContextChanged(this.m_currentContext, ldContext))
        {
            this.m_ldClient.identify(ldContext);
            this.m_currentContext = ldContext;
        }
    }

    private createAllFeatureFlags(request: Models.FeatureFlagRequest,
                                  flagsObject: LDClient.LDFlagSet): Models.FeatureFlag[]
    {
        return this.getFlagNames(request.feature_category)
                   .filter(flagName => FeatureFlagsHelper.isSupportedFlagType(FeatureFlagsHelper.extractFlagType(flagName)))
                   .map(flagName => FeatureFlagsHelper.createFeatureFlag(request.category, flagName, flagsObject[flagName] ?? false, false));
    }

    private createAllReleaseFlags(request: Models.ReleaseFlagRequest,
                                  flagsObject: LDClient.LDFlagSet): Models.ReleaseFlag[]
    {
        return LaunchDarklyFeatureFlagsProvider.m_releaseFlagNames
                                               .map(flagName => FeatureFlagsHelper.createReleaseFlag(request.category, flagName, flagsObject[flagName] ?? false, false));
    }

    //--// Flag name registry
    private static readonly m_flagNamesMap: ReadonlyMap<Models.FeatureCategory, readonly string[]> = new Map(
        [
            [
                Models.FeatureCategory.AI,
                <string[]>Object.values(Models.AIFeatureFlags)
            ],
            [
                Models.FeatureCategory.AI_AGENT,
                <string[]>Object.values(Models.AIAgentFeatureFlags)
            ],
            [
                Models.FeatureCategory.BROKER,
                <string[]>Object.values(Models.BrokerFeatureFlags)
            ],
            [
                Models.FeatureCategory.COMPLIANCE,
                <string[]>Object.values(Models.ComplianceFeatureFlags)
            ],
            [
                Models.FeatureCategory.CORESTACK_IDENTITY,
                <string[]>Object.values(Models.IdentityFeatureFlags)
            ],
            [
                Models.FeatureCategory.COST,
                <string[]>Object.values(Models.CostFeatureFlags)
            ],
            [
                Models.FeatureCategory.GOVERNANCE,
                <string[]>Object.values(Models.GovernanceFeatureFlags)
            ],
            [
                Models.FeatureCategory.HEATSTACK,
                <string[]>Object.values(Models.HeatstackFeatureFlags)
            ],
            [
                Models.FeatureCategory.OPERATIONS_GOVERNANCE,
                <string[]>Object.values(Models.OperationsGovernanceFeatureFlags)
            ],
            [
                Models.FeatureCategory.OTHERS,
                <string[]>Object.values(Models.OthersFeatureFlags)
            ],
            [
                Models.FeatureCategory.REPORT_GENERATION,
                <string[]>Object.values(Models.ReportGenerationFeatureFlags)
            ],
            [
                Models.FeatureCategory.REPORTS,
                <string[]>Object.values(Models.ReportsFeatureFlags)
            ],
            [
                Models.FeatureCategory.RESOURCE_INVENTORY,
                <string[]>Object.values(Models.ResourceInventoryFeatureFlags)
            ],
            [
                Models.FeatureCategory.SECURITY_GOVERNANCE,
                <string[]>Object.values(Models.SecurityGovernanceFeatureFlags)
            ],
            [
                Models.FeatureCategory.UI,
                <string[]>Object.values(Models.UiFeatureFlags)
            ]
        ]);

    private static readonly m_releaseFlagNames: readonly string[] = Object.values(Models.ReleaseFlags);

    private getFlagNames(featureCategory: Models.FeatureCategory): readonly string[]
    {
        const names = LaunchDarklyFeatureFlagsProvider.m_flagNamesMap.get(featureCategory);
        if (!names) throw new Error(LaunchDarklyFeatureFlagsProvider.ERR_INVALID_FEATURE_CATEGORY);
        return names;
    }

    private onInitialized(): void
    {
        console.log(LaunchDarklyFeatureFlagsProvider.LOG_INITIALIZED);
    }

    private onChange(changes: LDClient.LDFlagChangeset): void
    {
        if (!this.m_releaseFlagState && !this.m_featureFlagState) return;

        const updatedReleaseFlags: Models.ReleaseFlag[] = [];
        const updatedFeatureFlags: Models.FeatureFlag[] = [];

        for (const flagName in changes)
        {
            const newValue = changes[flagName].current;

            if (FeatureFlagsHelper.isReleaseFlag(flagName) && this.m_releaseFlagState)
            {
                const existingFlag = this.m_releaseFlagState()[flagName];
                if (existingFlag)
                {
                    updatedReleaseFlags.push(Models.ReleaseFlag.newInstance({
                                                                                ...existingFlag,
                                                                                enabled: newValue ?? existingFlag.default
                                                                            }));
                }
            }
            else if (this.m_featureFlagState)
            {
                const existingFlag = this.m_featureFlagState()[flagName];
                if (existingFlag)
                {
                    updatedFeatureFlags.push(Models.FeatureFlag.newInstance({
                                                                                ...existingFlag,
                                                                                enabled: newValue ?? existingFlag.default
                                                                            }));
                }
            }
        }

        if (updatedReleaseFlags.length > 0) this.updateReleaseFlagState(updatedReleaseFlags);
        if (updatedFeatureFlags.length > 0) this.updateFeatureFlagState(updatedFeatureFlags);
    }

    private onError(error: any): void
    {
        console.error(LaunchDarklyFeatureFlagsProvider.WARN_CLIENT_ERROR, error);

        this.m_pendingSingleRequests.forEach(({resolve}) => resolve());
        this.m_pendingSingleRequests = [];

        this.m_pendingAllRequests.forEach(({resolve}) => resolve());
        this.m_pendingAllRequests = [];
    }

    private onReady(): void
    {
        this.m_initialized = true;
        console.log(LaunchDarklyFeatureFlagsProvider.LOG_CLIENT_READY);

        for (const {
                       request,
                       resolve
                   } of this.m_pendingSingleRequests)
        {
            try
            {
                const flag = this.fetchFlagFromLaunchDarkly(request);
                this.updateFlagState(flag, request.category);
            }
            catch (error)
            {
                console.error(LaunchDarklyFeatureFlagsProvider.ERR_QUEUE_FLAG_FAILED(request.flag_name), error);
            }
            resolve();
        }
        this.m_pendingSingleRequests = [];

        for (const {
                       request,
                       resolve
                   } of this.m_pendingAllRequests)
        {
            try
            {
                const flags = this.fetchAllFlagsFromLaunchDarkly(request);
                this.updateFlagStates(flags, request.category);
            }
            catch (error)
            {
                console.error(LaunchDarklyFeatureFlagsProvider.ERR_QUEUE_FLAGS_FAILED, error);
            }
            resolve();
        }
        this.m_pendingAllRequests = [];
    }

    private updateFeatureFlagState(flags: Models.FeatureFlag[]): void
    {
        if (!this.m_featureFlagState) return;

        const currentFlags = this.m_featureFlagState();
        const updatedFlags = {...currentFlags};

        flags.forEach(flag =>
                      {
                          if (flag) updatedFlags[flag.flag_name] = flag;
                      });

        this.m_featureFlagState.set(updatedFlags);
    }

    private updateReleaseFlagState(flags: Models.ReleaseFlag[]): void
    {
        if (!this.m_releaseFlagState) return;

        const currentFlags = this.m_releaseFlagState();
        const updatedFlags = {...currentFlags};

        flags.forEach(flag =>
                      {
                          if (flag) updatedFlags[flag.flag_name] = flag;
                      });

        this.m_releaseFlagState.set(updatedFlags);
    }


    private getFlagFromState(request: Models.FeatureFlagRequestBase): Models.FeatureFlagBase | null
    {
        if (request.category === Models.FeatureFlagCategory.RELEASE && this.m_releaseFlagState)
        {
            const currentFlags = this.m_releaseFlagState();
            return currentFlags[request.flag_name] || null;
        }

        if (request.category === Models.FeatureFlagCategory.FEATURE && this.m_featureFlagState)
        {
            const currentFlags = this.m_featureFlagState();
            return currentFlags[request.flag_name] || null;
        }

        return null;
    }

    private fetchFlagFromLaunchDarkly(request: Models.FeatureFlagRequestBase): Models.FeatureFlagBase | null
    {
        const flagValue = this.m_ldClient.variation(request.flag_name, false);

        if (request.category === Models.FeatureFlagCategory.FEATURE)
        {
            return FeatureFlagsHelper.createFeatureFlag(request.category, request.flag_name, flagValue, false);
        }

        if (request.category === Models.FeatureFlagCategory.RELEASE)
        {
            return FeatureFlagsHelper.createReleaseFlag(request.category, request.flag_name, flagValue, false);
        }

        return null;
    }

    private getAllFlagsFromState(request: Models.FeatureFlagRequestBase): Lookup<Models.FeatureFlagBase> | null
    {
        if (request instanceof Models.FeatureFlagRequest && this.m_featureFlagState)
        {
            const expectedFlagNames = this.getFlagNames(request.feature_category)
                                          .filter(n => FeatureFlagsHelper.isSupportedFlagType(FeatureFlagsHelper.extractFlagType(n)));
            const currentFlags      = this.m_featureFlagState();
            const allFlagsPresent   = expectedFlagNames.length > 0 && expectedFlagNames.every(n => n in currentFlags);
            return allFlagsPresent ? {...currentFlags} : null;
        }

        if (request instanceof Models.ReleaseFlagRequest && this.m_releaseFlagState)
        {
            const expectedFlagNames = LaunchDarklyFeatureFlagsProvider.m_releaseFlagNames;
            const currentFlags      = this.m_releaseFlagState();
            const allFlagsPresent   = expectedFlagNames.length > 0 && expectedFlagNames.every(n => n in currentFlags);
            return allFlagsPresent ? {...currentFlags} : null;
        }

        return null;
    }

    private fetchAllFlagsFromLaunchDarkly(request: Models.FeatureFlagRequestBase): Lookup<Models.FeatureFlagBase>
    {
        const allLDFlags: LDClient.LDFlagSet = this.m_ldClient.allFlags();

        if (request instanceof Models.FeatureFlagRequest)
        {
            const flags = this.createAllFeatureFlags(request, allLDFlags);
            this.updateFeatureFlagState(flags);
            return this.m_featureFlagState ? {...this.m_featureFlagState()} : {};
        }
        else if (request instanceof Models.ReleaseFlagRequest)
        {
            const flags = this.createAllReleaseFlags(request, allLDFlags);
            this.updateReleaseFlagState(flags);
            return this.m_releaseFlagState ? {...this.m_releaseFlagState()} : {};
        }

        return {};
    }

    private updateFlagState(flag: Models.FeatureFlagBase,
                            category: Models.FeatureFlagCategory): boolean
    {
        if (flag === null || flag === undefined)
        {
            throw new Error(LaunchDarklyFeatureFlagsProvider.ERR_INVALID_FLAG);
        }

        if (category === Models.FeatureFlagCategory.FEATURE)
        {
            this.updateFeatureFlagState([flag as Models.FeatureFlag]);
            return true;
        }

        if (category === Models.FeatureFlagCategory.RELEASE)
        {
            this.updateReleaseFlagState([flag as Models.ReleaseFlag]);
            return true;
        }

        return false;
    }

    private updateFlagStates(flags: Lookup<Models.FeatureFlagBase>,
                             category: Models.FeatureFlagCategory): boolean
    {
        const flagList = Object.values(flags)
                               .filter(f => f != null);

        if (category === Models.FeatureFlagCategory.FEATURE)
        {
            this.updateFeatureFlagState(flagList as Models.FeatureFlag[]);
        }
        else if (category === Models.FeatureFlagCategory.RELEASE)
        {
            this.updateReleaseFlagState(flagList as Models.ReleaseFlag[]);
        }

        return true;
    }

    //--// Public API
    public setReleaseFlagsState(state: WritableSignal<Lookup<Models.ReleaseFlag>>): void
    {
        this.m_releaseFlagState = state;
    }

    public setFeatureFlagsState(state: WritableSignal<Lookup<Models.FeatureFlag>>): void
    {
        this.m_featureFlagState = state;
    }

    public initializeProvider(context: Models.FeatureFlagContext): void
    {
        if (this.m_initialized || this.m_initializing) return;

        this.m_initializing = true;

        if (this.isLdEnabled)
        {
            // Translate FeatureFlagContext to LDContext
            const ldContext: LDClient.LDContext = FeatureFlagsHelper.translateToLDContext(context);

            // Store the current context
            this.m_currentContext = ldContext;

            // Initialize LaunchDarkly client with translated context
            this.m_ldClient = LDClient.initialize(AppService.config.ldClientId, ldContext);

            // Register event handlers
            this.m_ldClient.waitForInitialization();
            this.m_ldClient.on(LaunchDarklyFeatureFlagsProvider.LD_EVENT_INITIALIZED, this.onInitialized.bind(this));
            this.m_ldClient.on(LaunchDarklyFeatureFlagsProvider.LD_EVENT_READY, this.onReady.bind(this));
            this.m_ldClient.on(LaunchDarklyFeatureFlagsProvider.LD_EVENT_CHANGE, this.onChange.bind(this));
            this.m_ldClient.on(LaunchDarklyFeatureFlagsProvider.LD_EVENT_ERROR, this.onError.bind(this));
        }
    }

    public async queueFlag(request: Models.FeatureFlagRequestBase): Promise<void>
    {
        try
        {
            FeatureFlagsHelper.validateFlagRequest(request);

            if (!this.m_initialized)
            {
                if (!this.isLdEnabled) return;

                return new Promise<void>(resolve => this.m_pendingSingleRequests.push({
                                                                                          request,
                                                                                          resolve
                                                                                      }));
            }

            const flag = this.fetchFlagFromLaunchDarkly(request);
            this.updateFlagState(flag, request.category);
        }
        catch (error)
        {
            console.error(LaunchDarklyFeatureFlagsProvider.ERR_QUEUE_FLAG_FAILED(request.flag_name), error);
        }
    }

    public async queueFlags(request: Models.FeatureFlagRequestBase): Promise<void>
    {
        try
        {
            FeatureFlagsHelper.validateAllFlagsRequest(request);

            if (!this.m_initialized)
            {
                if (!this.isLdEnabled) return;

                await new Promise<void>(resolve => this.m_pendingAllRequests.push({
                                                                                      request,
                                                                                      resolve
                                                                                  }));
                return;
            }

            const flags = this.fetchAllFlagsFromLaunchDarkly(request);
            this.updateFlagStates(flags, request.category);
        }
        catch (error)
        {
            console.error(LaunchDarklyFeatureFlagsProvider.ERR_QUEUE_FLAGS_FAILED, error);
        }
    }
}
